import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, ArrowPathIcon } from "@heroicons/react/20/solid";
import { AuthContext } from "../../contexts/auth";
import { getTeamContext, getTeamCalendar } from "../../services/api_dipendenti";
import { Popover } from "@headlessui/react";
import Footer from "../../components/footer";
import "../../styles/il_mio_team.css";
import ComboboxAutocomplete from "../../components/combobox_autocomplete";

const PAGE_SIZE = 100;
const SEARCH_MIN_LENGTH = 2;

const STATUS_LABELS = {
  present: 'Presente',
  smart: 'Smart working',
  away: 'Trasferta',
  absent: 'Assente',
  off: 'Non lavorativo',
};

const LEGEND = ['present', 'smart', 'away', 'absent'];

const WEEKDAYS = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];

const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function fullName(person) {
  return [person.first_name, person.last_name].filter(Boolean).join(' ') || 'Collega';
}

function initials(person) {
  const letters = [person.first_name, person.last_name]
    .filter(Boolean)
    .map((part) => part.trim().charAt(0).toUpperCase())
    .join('');
  return letters || '?';
}

function formatSlots(slots) {
  if (!slots || slots.length === 0) return 'Orario non disponibile';
  return slots.map((slot) => `${slot.start_at}–${slot.end_at}`).join(', ');
}

// Le eccezioni sono già tagliate al giorno: un datetime_to a mezzanotte del giorno dopo diventa "24:00".
function formatException(exception, date) {
  const from = moment.parseZone(exception.datetime_from);
  const to = moment.parseZone(exception.datetime_to);
  const end = to.format('YYYY-MM-DD') > date ? '24:00' : to.format('HH:mm');
  return `${from.format('HH:mm')}–${end}`;
}

function describeDay(day) {
  if (!day) return 'Nessun contratto attivo';
  const holidays = (day.holidays || []).map((holiday) => `Festività: ${holiday.name}`);
  if (day.status === 'off') return ['Non lavorativo', ...holidays].join('\n');
  const exceptions = (day.exceptions || []).map((exception) => {
    const label = exception.status === 'away' ? 'Trasferta parziale' : 'Assenza parziale';
    return `${label}: ${formatException(exception, day.date)}`;
  });
  return [`${STATUS_LABELS[day.status] || day.status} · ${formatSlots(day.time_slots)}`, ...exceptions, ...holidays].join('\n');
}

// Due giorni consecutivi si "uniscono" in una banda continua se hanno lo stesso stato lavorativo
function joinable(a, b) {
  return Boolean(a && b)
    && a.status === b.status
    && a.status !== 'off'
    && a.exceptions.length === 0
    && b.exceptions.length === 0;
}

function errorMessage(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status === 401) return 'Sessione scaduta: effettua di nuovo l\'accesso.';
  if (status === 429) return 'Troppe richieste in poco tempo, riprova tra qualche istante.';
  if (data && typeof data === 'object') {
    if (data.detail) return data.detail;
    const first = Object.values(data)[0];
    if (Array.isArray(first) && first.length > 0) return first[0];
  }
  return 'Non è stato possibile caricare le presenze.';
}

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function ArIlMioTeam() {
  const { token } = useContext(AuthContext);

  const [context, setContext] = useState({ loading: true, data: null, error: null });
  const [month, setMonth] = useState(() => moment().startOf('month'));
  const [ou, setOu] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [calendar, setCalendar] = useState({
    loading: true, loadingMore: false, error: null, rows: [], count: 0, page: 1, hasNext: false, updatedAt: null,
  });
  const [tooltip, setTooltip] = useState(null);

  const requestId = useRef(0);
  const scrollRef = useRef(null);

  const debouncedSearch = useDebouncedValue(searchInput.trim(), 350);
  const search = debouncedSearch.length >= SEARCH_MIN_LENGTH ? debouncedSearch : '';
  const enabled = context.data?.enabled === true;

  const range = useMemo(() => ({
    start_date: month.clone().startOf('month').format('YYYY-MM-DD'),
    end_date: month.clone().endOf('month').format('YYYY-MM-DD'),
  }), [month]);

  const days = useMemo(() => {
    const today = moment().format('YYYY-MM-DD');
    return Array.from({ length: month.daysInMonth() }, (_, index) => {
      const date = month.clone().date(index + 1);
      const weekday = date.day();
      const iso = date.format('YYYY-MM-DD');
      return { iso, number: index + 1, weekday: WEEKDAYS[weekday], weekend: weekday === 0 || weekday === 6, today: iso === today };
    });
  }, [month]);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    getTeamContext(token, { signal: controller.signal })
      .then((data) => setContext({ loading: false, data, error: null }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setContext({ loading: false, data: null, error: errorMessage(error) });
      });
    return () => controller.abort();
  }, [token]);

  const fetchPage = useCallback((page, signal) => {
    const params = { ...range, page, page_size: PAGE_SIZE };
    if (ou) params.ou = ou;
    if (search) params.search = search;
    return getTeamCalendar(token, params, { signal });
  }, [token, range, ou, search]);

  // Prima pagina: si ricarica a ogni cambio di mese, sede, ricerca o "Aggiorna"
  useEffect(() => {
    if (!token || !enabled) return;
    const controller = new AbortController();
    const id = ++requestId.current;
    setCalendar((prev) => ({ ...prev, loading: true, error: null }));
    fetchPage(1, controller.signal)
      .then((data) => {
        if (id !== requestId.current) return;
        setCalendar({
          loading: false, loadingMore: false, error: null,
          rows: data.results, count: data.count, page: 1, hasNext: Boolean(data.next), updatedAt: new Date(),
        });
      })
      .catch((error) => {
        if (controller.signal.aborted || id !== requestId.current) return;
        setCalendar((prev) => ({ ...prev, loading: false, error: errorMessage(error) }));
      });
    return () => controller.abort();
  }, [token, enabled, fetchPage, reloadKey]);

  const loadMore = () => {
    const id = requestId.current;
    const nextPage = calendar.page + 1;
    setCalendar((prev) => ({ ...prev, loadingMore: true }));
    fetchPage(nextPage)
      .then((data) => {
        if (id !== requestId.current) return;
        setCalendar((prev) => ({
          ...prev, loadingMore: false, rows: [...prev.rows, ...data.results], count: data.count, page: nextPage, hasNext: Boolean(data.next),
        }));
      })
      .catch((error) => {
        if (id !== requestId.current) return;
        setCalendar((prev) => ({ ...prev, loadingMore: false, error: errorMessage(error) }));
      });
  };

  // Nel mese corrente porta "oggi" in vista (subito dopo la colonna fissa dei nomi)
  const firstRowsLoaded = calendar.rows.length > 0;
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || !firstRowsLoaded) return;
    const todayHead = scroller.querySelector('th.today');
    const personHead = scroller.querySelector('thead th.imt-person');
    scroller.scrollLeft = todayHead && personHead ? Math.max(0, todayHead.offsetLeft - personHead.offsetWidth - 42 * 2) : 0;
  }, [month, firstRowsLoaded]);

  const rows = useMemo(() => calendar.rows.map((person) => {
    const byDate = new Map(person.days.map((day) => [day.date, day]));
    return { person, cells: days.map((day) => byDate.get(day.iso) || null) };
  }), [calendar.rows, days]);

  const showTooltip = (event) => {
    const cell = event.target.closest('[data-tip]');
    if (!cell) {
      setTooltip(null);
      return;
    }
    const rect = cell.getBoundingClientRect();
    const below = rect.top < 90;
    setTooltip({
      text: cell.dataset.tip,
      left: Math.min(Math.max(rect.left + rect.width / 2, 130), window.innerWidth - 130),
      top: below ? rect.bottom + 6 : rect.top - 6,
      below,
    });
  };

  const shiftMonth = (amount) => setMonth((current) => current.clone().add(amount, 'month'));
  const isCurrentMonth = month.isSame(moment(), 'month');
  const units = context.data?.organizational_units || [];
  const searchTooShort = searchInput.trim().length > 0 && searchInput.trim().length < SEARCH_MIN_LENGTH;

  const selectedUnit = units.find(
    (unit) => String(unit.id) === String(ou)
  ) || null;

  const syncLabel = calendar.loading
    ? 'Aggiornamento in corso…'
    : calendar.updatedAt
      ? `Aggiornato alle ${calendar.updatedAt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
      : '';

  const renderBody = () => {
    if (context.loading || (calendar.loading && calendar.rows.length === 0 && !calendar.error)) {
      return (
        <div className="flex min-h-[320px] items-center justify-center gap-3" role="status">
          <span className="imt-spinner" aria-hidden="true" />
          <span className="text-sm font-semibold text-gray-800">Caricamento del calendario…</span>
        </div>
      );
    }
    if (calendar.error && calendar.rows.length === 0) {
      return (
        <div className="px-6 py-14 text-center">
          <p className="text-sm font-semibold text-gray-900">{calendar.error}</p>
          <button type="button" onClick={() => setReloadKey((key) => key + 1)} className="mt-4 rounded-md bg-white px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
            Riprova
          </button>
        </div>
      );
    }
    if (calendar.rows.length === 0) {
      return <div className="px-6 py-14 text-center text-sm text-gray-500">Nessun collega corrisponde ai filtri</div>;
    }
    return (
      <>
        <div ref={scrollRef} className={classNames('imt-scroll', calendar.loading && 'is-refreshing')}>
          <table className="imt-calendar" aria-label={`Disponibilità del team, ${MESI[month.month()]} ${month.year()}`}>
            <thead>
              <tr>
                <th className="imt-person" scope="col">Colleghi</th>
                {days.map((day) => (
                  <th key={day.iso} scope="col" className={classNames('imt-day-head', day.weekend && 'weekend', day.today && 'today')}>
                    <span>{day.number}</span>
                    <small>{day.weekday}</small>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody onMouseOver={showTooltip} onMouseLeave={() => setTooltip(null)}>
              {rows.map(({ person, cells }) => (
                <tr key={person.id} className={classNames(person.is_me && 'is-me')}>
                  <th scope="row" className="imt-person">
                    <div className="imt-person-cell">
                      <div className="imt-avatar">{initials(person)}</div>
                      <div className="min-w-0">
                        <p className="imt-name">
                          {fullName(person)}
                          {person.is_me && <span className="imt-me-badge">Tu</span>}
                        </p>
                        <p className="imt-meta">{person.ou_name || 'Nessuna sede'}</p>
                      </div>
                    </div>
                  </th>
                  {cells.map((cell, index) => {
                    const day = days[index];
                    const status = cell ? cell.status : 'missing';
                    const description = describeDay(cell);
                    const hasAwayException = cell?.exceptions?.some((exception) => exception.status === 'away');
                    const hasAbsentException = cell?.exceptions?.some((exception) => !exception.status || exception.status === 'absent');

                    return (
                      <td
                        key={day.iso}
                        data-tip={description}
                        aria-label={`${fullName(person)}, ${day.number}: ${description.replace(/\n/g, ', ')}`}
                        className={classNames(
                          'imt-status',
                          status,
                          day.weekend && 'weekend',
                          cell && cell.status === 'off' && cell.holidays.length > 0 && 'holiday',
                          hasAbsentException && 'has-exception',
                          hasAwayException && 'has-away-exception',
                          joinable(cells[index - 1], cell) && 'join-left',
                          joinable(cell, cells[index + 1]) && 'join-right',
                        )}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 text-xs text-gray-500">
          <span>Mostrati {calendar.rows.length} di {calendar.count} colleghi</span>
          {calendar.error && <span className="font-semibold text-red-600">{calendar.error}</span>}
          {calendar.hasNext && (
            <button type="button" onClick={loadMore} disabled={calendar.loadingMore} className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-wait disabled:opacity-60">
              {calendar.loadingMore ? 'Caricamento…' : 'Mostra altri colleghi'}
            </button>
          )}
        </div>
      </>
    );
  };

  const renderDisabled = (message) => (
    <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
      <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
        <span className="block text-sm font-semibold text-gray-900">{message}</span>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col">
      <div className="imt mb-8 flex-grow">
        <Popover as="header" className={`bg-indigo-200`}>
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
            <div className="relative flex flex-wrap items-center justify-center lg:justify-between">
              <div className="w-full -5 lg:border-t lg:border-white lg:border-opacity-20">
                <div className="flex justify-center my-2 py-2">
                  <h2 className="text-center text-2xl font-semibold text-indigo-800">Il mio team</h2>
                </div>
              </div>
            </div>
          </div>
        </Popover>

        {context.error && renderDisabled(context.error)}
        {!context.loading && context.data && !enabled && renderDisabled('La funzionalità "Il mio team" non è attiva')}

        {(context.loading || enabled) && (
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <section className="pb-6 pt-8 ">
              <div className="flex flex-col rounded-xl border border-gray-300 bg-white pt-4 pb-2 px-4 sm:pt-5 sm:pb-2 sm:px-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-normal font-semibold uppercase text-indigo-800">
                      Il team, giorno per giorno
                    </h3>
                    <p className="text-xs text-gray-500">
                      Una panoramica delle presenze previste nel mese selezionato
                    </p>
                  </div>

                  <div className="inline-flex max-w-full flex-wrap items-center sm:justify-end justify-start gap-2">

                    {/* NAVIGAZIONE MESE */}
                    <div className="inline-flex items-center overflow-hidden rounded-lg border border-indigo-200 bg-white shadow-sm">

                      <button
                        type="button"
                        onClick={() => shiftMonth(-1)}
                        title="Mese precedente"
                        className="flex h-10 w-10 shrink-0 items-center justify-center bg-indigo-50 text-indigo-700 transition-colors hover:bg-indigo-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500"
                      >
                        <span className="sr-only">Mese precedente</span>
                        <ChevronLeftIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </button>

                      <button
                        type="button"
                        onClick={() => setMonth(moment().startOf('month'))}
                        disabled={isCurrentMonth}
                        title={isCurrentMonth ? undefined : 'Torna al mese corrente'}
                        className="flex h-10 min-w-[9.5rem] items-center justify-center border-x border-indigo-200 px-4 text-sm font-semibold capitalize text-indigo-800 transition-colors hover:bg-indigo-100 disabled:cursor-default disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500"
                      >
                        {MESI[month.month()]} {month.year()}
                      </button>

                      <button
                        type="button"
                        onClick={() => shiftMonth(1)}
                        title="Mese successivo"
                        className="flex h-10 w-10 shrink-0 items-center bg-indigo-50 justify-center text-indigo-700 transition-colors hover:bg-indigo-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500"
                      >
                        <span className="sr-only">Mese successivo</span>
                        <ChevronRightIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </button>

                    </div>

                    {/* PULSANTE AGGIORNA E ULTIMO AGGIORNAMENTO */}
                    <div className="flex flex-col items-center gap-1">

                      <button
                        type="button"
                        onClick={() => setReloadKey((key) => key + 1)}
                        disabled={!enabled || calendar.loading}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 text-sm font-semibold text-indigo-700 shadow-sm transition-colors hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-wait disabled:opacity-50"
                      >
                        <ArrowPathIcon
                          className={classNames(
                            'h-4 w-4',
                            calendar.loading && enabled && 'animate-spin'
                          )}
                          aria-hidden="true"
                        />

                        <span>Aggiorna</span>
                      </button>
                    </div>
                  </div>
                </div>
                {/* SECONDA RIGA: ULTIMO AGGIORNAMENTO */}
                <div className="flex sm:justify-end justify-center pt-2">
                  <span
                    className="whitespace-nowrap text-xs text-gray-500"
                    aria-live="polite"
                  >
                    {syncLabel}
                  </span>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-gray-300 bg-white">
              <div className="grid grid-cols-1 gap-3 border-b border-gray-200 p-4 md:grid-cols-2">
                <div className="relative min-w-0">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Cerca un collega"
                    aria-label="Cerca un collega"
                    className="block w-full rounded-md border-0 py-2 pl-10 pr-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
                  />
                  {searchTooShort && <p className="absolute -bottom-4 left-1 text-[10px] text-gray-400 pb-1">Inserisci almeno {SEARCH_MIN_LENGTH} caratteri</p>}
                </div>
                {units.length > 1 && (
                  <div className="relative min-w-0 w-full">
                    <ComboboxAutocomplete
                      options={[
                        { id: "__ALL__", name: "Tutte le sedi" },
                        ...units,
                      ]}
                      value={
                        selectedUnit ?? {
                          id: "__ALL__",
                          name: "Tutte le sedi",
                        }
                      }
                      alwaysFirstIds={["__ALL__"]}
                      onChange={(next) => {
                        if (!next || next.id === "__ALL__") {
                          setOu('');
                        } else {
                          setOu(String(next.id));
                        }
                      }}
                      placeholder="Seleziona sede..."
                      bgActiveColor="bg-indigo-50"
                      textActiveColor="text-gray-700"
                      positionOptions="absolute"
                      zIndex="z-50"
                      borderClass="border-gray-200 rounded-md"
                    />
                  </div>
                )}
                <div
                  className="col-span-1 flex w-full flex-wrap gap-x-4 gap-y-2 text-xs text-gray-500 md:col-span-2"
                  aria-label="Legenda"
                >
                  {LEGEND.map((status) => (
                    <span key={status} className="inline-flex items-center gap-1.5"><i className={classNames('imt-dot', status)} />{STATUS_LABELS[status]}</span>
                  ))}
                  <span className="inline-flex items-center gap-1.5"><i className="imt-dot exception" />Assenza parziale</span>
                  <span className="inline-flex items-center gap-1.5"><i className="imt-dot away-exception" />Trasferta parziale</span>
                  <span className="inline-flex items-center gap-1.5"><i className="imt-dot off" />Non lavorativo</span>
                </div>
              </div>
              {renderBody()}
            </section>
          </div>
        )}

        {tooltip && (
          <div
            className="imt-tooltip"
            role="tooltip"
            style={{ left: tooltip.left, top: tooltip.top, transform: `translate(-50%, ${tooltip.below ? '0' : '-100%'})` }}
          >
            {tooltip.text}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
